import { NgForOf, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs';
import { ISqcdpShifts } from 'scw-digital-boards-widgets';
import { IDropdownOption, MatDropdown, TDropdownId } from '../mat-dropdown/mat-dropdown.component';
import { TChildDropdowns, TConfig, TDropdownConfig } from './shift-selection.interface';
import { ShiftSelectionStore } from './shift-selection.store';

@Component({
  imports: [MatDropdown, NgForOf, NgIf],
  providers: [ShiftSelectionStore],
  selector: 'app-shift-selection',
  standalone: true,
  templateUrl: './shift-selection.component.html',
})
export class ShiftSelectionComponent implements OnInit {
  @Input() formControl!: FormControl<ISqcdpShifts | null>;
  @Input() isFormSubmitted = false;
  @Input() isEditMode = false;

  public shiftOptions: number[] = [1, 2, 3];
  public shiftSelection: TDropdownConfig<number> = {
    configurationInput: this.store.getDefaultMultiSelectConfig({
      enableSearchFilter: false,
      repositionDropdownList: false,
      singleSelection: true,
      text: 'shiftSelection',
    }),
    formControl: new FormControl<{ id: number; name: string }[] | null>([], {
      validators: [Validators.required],
    }),
    options: [
      {
        id: 0,
        name: this.translate.instant('widget.sqcdpMonthly.shiftSelection.noShift'),
      },
      {
        id: 1,
        name: this.translate.instant('widget.sqcdpMonthly.shiftSelection.shift1'),
      },
      {
        id: 2,
        name: this.translate.instant('widget.sqcdpMonthly.shiftSelection.shift2'),
      },
      {
        id: 3,
        name: this.translate.instant('widget.sqcdpMonthly.shiftSelection.shift3'),
      },
    ],
  };

  public childDropdowns: TChildDropdowns<TDropdownId> = {} as TChildDropdowns<TDropdownId>;
  public selectedConfiguration: Record<number, any> = {};
  public selectedOptions: IDropdownOption<number>[] = [];
  public selectedOptionId: number | undefined;
  public maxOptionCount = 0;

  constructor(
    private readonly translate: TranslateService,
    private readonly store: ShiftSelectionStore,
  ) {}

  public ngOnInit(): void {
    this.shiftSelection.formControl.valueChanges
      .pipe(skip(this.isEditMode ? 2 : 1))
      .subscribe((selected: IDropdownOption<number>[] | null) =>
        this.onShiftSelectionSelectionChange(selected as IDropdownOption<number>[] | null),
      );

    if (this.formControl.value !== null) {
      this.initializeShiftSelection(this.formControl.value);
    }
  }

  private initializeShiftSelection(formValues: ISqcdpShifts): void {
    this.shiftSelection.formControl.setValue([
      this.shiftSelection.options.find((option) => option.id === formValues.layerCount)!,
    ]);
    this.maxOptionCount = formValues.layerCount;
  }

  private onShiftSelectionSelectionChange(selected: IDropdownOption<number>[] | null): void {
    const newSelected: IDropdownOption<number>[] = selected || [];
    const newShiftCount = newSelected.length > 0 ? newSelected[0].id : 0;
    this.maxOptionCount = newShiftCount;
    this.shiftOptions.forEach((layerId) => {
      if (layerId > newShiftCount) {
        this.clearChildSelection(layerId);
      }
    });

    newSelected.forEach((option) => {
      this.loadAndSetChildrenDropdownData(option.id);
      this.selectedOptionId = option.id;
      this.maxOptionCount = this.selectedOptionId;
      this.selectedOptions = newSelected;

      if (this.selectedOptionId === 0) {
        this.formControl.setValue({ layerCount: 0 });
        this.maxOptionCount = 0;
        this.formControl.setErrors(null);
      }
    });

    if (this.selectedOptionId) {
      this.updateChildSelection();
    }
  }

  private onChildSelectionChange(
    optionId: number,
    childDropdownSelection: IDropdownOption<TDropdownId>[] | null,
  ): void {
    this.selectedConfiguration[optionId] = childDropdownSelection?.map((c: IDropdownOption<TDropdownId>) => c.id) || [];
    this.updateChildSelection();
  }

  private clearChildSelection(optionId: number): void {
    this.selectedConfiguration[optionId] = [];

    if (this.childDropdowns[optionId]?.formControl) {
      this.childDropdowns[optionId].formControl.setValue([]);
    }

    if (this.childDropdowns[optionId]) {
      delete this.childDropdowns[optionId];
    }
  }

  private loadAndSetChildrenDropdownData(optionId: number): void {
    if (optionId) {
      for (let i = 1; i <= optionId; i++) {
        const config: TConfig = this.getConfigForOption(i);
        this.loadAndSubscribe(i, config);
      }
    }
  }

  private getConfigForOption(layerId: number): TConfig {
    return {
      configurationInput: () =>
        this.store.getDefaultMultiSelectConfig({
          isClientSide: true,
          repositionDropdownList: false,
          text: `layer${layerId}ShiftSelection`,
        }),
      getSelectedIds: (shifts: ISqcdpShifts): number[] => {
        const key = `layer${layerId}Shift` as keyof ISqcdpShifts;

        return (shifts[key] as number[]) ?? [];
      },
      loadFn: (searchText?: string): void => {
        this.store.loadShift({
          fields: ['name'],
          limit: 1000,
          ...(searchText ? { search: { searchedFields: ['name'], searchText } } : {}),
        });
      },
      observable$: this.store.shiftsDropdown$,
    };
  }

  private loadAndSubscribe(layerId: number, config: TConfig): void {
    const shiftSelection: ISqcdpShifts | null = this.formControl.value;
    const formControl: FormControl<IDropdownOption<TDropdownId>[] | null> = this.createChildFormControl(layerId);
    this.initializeChildrenDropdown(layerId, config, formControl, shiftSelection);

    config.loadFn?.();
  }

  private createChildFormControl(optionId: number): FormControl<IDropdownOption<TDropdownId>[] | null> {
    const formControl = new FormControl<IDropdownOption<TDropdownId>[] | null>([], {
      validators: [Validators.required],
    });

    formControl.valueChanges.subscribe((childSelected) => {
      this.onChildSelectionChange(optionId, childSelected);
    });

    return formControl;
  }

  private initializeChildrenDropdown(
    layerId: number,
    config: TConfig,
    formControl: FormControl<IDropdownOption<TDropdownId>[] | null>,
    shiftSelection: ISqcdpShifts | null,
  ): void {
    this.childDropdowns[layerId] = {
      configurationInput: config.configurationInput(),
      currentSearchText: '',
      formControl,
      hasLoadedInitialSelection: false,
      loadFn: (searchText?: string): void => {
        this.childDropdowns[layerId]!.currentSearchText = searchText ?? '';
        config.loadFn?.(searchText);
      },
      options: [],
    };
    this.subscribeToOptions(layerId, config, formControl, shiftSelection);
  }

  private subscribeToOptions(
    optionId: number,
    config: TConfig,
    formControl: FormControl<IDropdownOption<TDropdownId>[] | null>,
    shiftSelection: ISqcdpShifts | null,
  ): void {
    config.observable$?.subscribe((options) => {
      const dropdown = this.childDropdowns[optionId]!;
      const searchText = dropdown.currentSearchText ?? '';
      const selectedValues = formControl.value ?? [];

      dropdown.options = [
        ...options,
        ...(searchText ? [] : selectedValues.filter((v) => !options.some((opt) => String(opt.id) === String(v.id)))),
      ];

      this.loadInitialSelectionIfNeeded(dropdown, config, formControl, shiftSelection);
    });
  }

  private loadInitialSelectionIfNeeded(
    dropdown: (typeof this.childDropdowns)[number],
    config: TConfig,
    formControl: FormControl<IDropdownOption<TDropdownId>[] | null>,
    shiftSelection: ISqcdpShifts | null,
  ): void {
    if (dropdown?.hasLoadedInitialSelection || !shiftSelection || !config.getSelectedIds || !dropdown?.options.length) {
      return;
    }

    const selectedIds: TDropdownId[] = config.getSelectedIds(shiftSelection).map((id) => id);
    const finalSelection = dropdown.options.filter((opt) => selectedIds.includes(opt.id));

    formControl.setValue(finalSelection, { emitEvent: true });
    dropdown.hasLoadedInitialSelection = true;
  }

  private updateChildSelection(): void {
    const totalLayers = this.maxOptionCount ?? 0;
    let isFormInvalid = false;

    for (let currentLayer = 1; currentLayer <= totalLayers; currentLayer++) {
      if (this.hasIncompleteSelection(currentLayer)) {
        isFormInvalid = true;
        break;
      }

      this.formControl.setValue(this.registerLastSelection(currentLayer) as ISqcdpShifts);
    }

    if (isFormInvalid) {
      this.formControl.setErrors({ incomplete: true });

      return;
    }

    this.formControl.setErrors(null);
  }

  private registerLastSelection(optionId: number): ISqcdpShifts {
    const result: ISqcdpShifts = { layerCount: optionId };

    Object.keys(this.selectedConfiguration).forEach((value: string, _index: number, _array: string[]) => {
      const layerSelectionArray: number[] & number = this.selectedConfiguration[parseInt(value)] ?? [];

      if (layerSelectionArray.length > 0) {
        const layer = `layer${parseInt(value)}Shift` as keyof ISqcdpShifts;
        result[layer] = layerSelectionArray ?? [];
      }
    });

    return result;
  }

  private hasIncompleteSelection(layerId: number): boolean {
    if (!this.selectedConfiguration[layerId]) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return !this.selectedConfiguration[layerId].length;
  }
}
